import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { IconChevronDown } from '@tabler/icons-react';
import { capitalize } from 'lodash';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { t } from 'i18next';

import { CallsFilterFormValue } from '@features/Admin/Campaign/model/type';
import { getCallsFiltersByCampaignType } from '@pages/Admin/Campaign/ui/utils';
import { CallEndedReasonsType } from '@shared/services/apiService/missing-types';
import { CallStatus, CallType, CampaignDto } from '@shared/services/apiService';
import CustomSelect from '../forms/theme-elements/CustomSelect';
import { CallEndedReasons, CallStatuses } from './consts';
import { useStyles } from './styles';

interface CallsFilterProps {
  campaignData: CampaignDto;
  formMethods: UseFormReturn<CallsFilterFormValue>;
  isExtendedSearch: boolean;
  onExtendedSearchChange: (value: boolean) => void;
}

type DateFilterKey = 'start' | 'end';

export const CallsFilter: React.FC<CallsFilterProps> = ({
  campaignData,
  formMethods,
  isExtendedSearch,
  onExtendedSearchChange,
}: CallsFilterProps) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { setValue, watch } = formMethods;
  const { statuses, types, endedReason, start, end, callIdSearch } = watch();
  const [callIdSearchText, setCallIdSearchText] = useState(callIdSearch || '');
  const previousCallIdSearchRef = useRef(callIdSearch);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (callIdSearchText !== callIdSearch) {
        setValue('callIdSearch', callIdSearchText);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [callIdSearchText, callIdSearch, setValue]);

  useEffect(() => {
    if (callIdSearch === previousCallIdSearchRef.current) return;
    previousCallIdSearchRef.current = callIdSearch;
    setCallIdSearchText(callIdSearch || '');
  }, [callIdSearch]);

  useEffect(() => {
    if (!isExtendedSearch) {
      setCallIdSearchText('');
    }
  }, [isExtendedSearch]);

  const handleDate = (key: DateFilterKey, date: Dayjs | null): void => {
    setValue(key, date ? date.toISOString() : null);
  };

  const parseDate = (date: string | null | undefined) => (date ? dayjs(date) : null);
  const startDate = parseDate(start);
  const endDate = parseDate(end);
  const isEndBeforeStart = Boolean(
    startDate?.isValid() && endDate?.isValid() && endDate.isBefore(startDate),
  );

  const renderSelectValue = (value: string, placeholder: string) =>
    value ? (
      capitalize(value).replaceAll('-', ' ')
    ) : (
      <Typography color={theme.palette.extendedGrey?.neutral}>{placeholder}</Typography>
    );

  const renderDatePicker = (
    key: 'start' | 'end',
    label: string,
    value: string | null | undefined,
  ) => (
    <DemoContainer
      components={['DateTimePicker']}
      sx={{
        '> .MuiTextField-root': { minWidth: 'unset !important' },
        overflow: 'clip',
      }}>
      <DesktopDateTimePicker
        label={label}
        value={parseDate(value)}
        sx={{ width: '100%' }}
        format="DD/MM/YYYY hh:mm A"
        onChange={(newValue) => handleDate(key, newValue)}
        closeOnSelect={false}
        slotProps={{
          textField: {
            size: 'small',
            error: key === 'end' && isEndBeforeStart,
            helperText:
              key === 'end' && isEndBeforeStart
                ? t('campaignPage.ongoingCallsTable.filter.endBeforeStart')
                : undefined,
            inputProps: { placeholder: 'DD/MM/YYYY hh:mm aa' },
            sx: { '& .MuiOutlinedInput-input': { paddingRight: 0 } },
          },
        }}
      />
    </DemoContainer>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined" size="small" className={classes.filter_select}>
            <InputLabel id="callType-label" shrink={!!types}>
              {t('campaignPage.ongoingCallsTable.filter.type')}
            </InputLabel>
            <CustomSelect
              id="callType-select"
              labelId="callType-label"
              input={
                <OutlinedInput
                  label={t('campaignPage.ongoingCallsTable.filter.type')}
                  notched={!!types}
                />
              }
              value={types ?? ''}
              name="types"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(event: SelectChangeEvent) =>
                setValue('types', event.target.value as CallType)
              }
              renderValue={(value: string) =>
                renderSelectValue(value, t('campaignPage.ongoingCallsTable.filter.type'))
              }>
              {getCallsFiltersByCampaignType(campaignData.type)?.map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter).replaceAll('-', ' ')}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined" size="small" className={classes.filter_select}>
            <InputLabel id="callStatus-label" shrink={!!statuses}>
              {t('campaignPage.ongoingCallsTable.filter.status')}
            </InputLabel>
            <CustomSelect
              id="callStatus-select"
              labelId="callStatus-label"
              input={
                <OutlinedInput
                  label={t('campaignPage.ongoingCallsTable.filter.status')}
                  notched={!!statuses}
                />
              }
              value={statuses ?? ''}
              name="statuses"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(event: SelectChangeEvent) =>
                setValue('statuses', event.target.value as CallStatus)
              }
              renderValue={(value: string) =>
                renderSelectValue(value, t('campaignPage.ongoingCallsTable.filter.status'))
              }>
              {Object.values(CallStatuses).map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter)}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined" size="small" className={classes.filter_select}>
            <InputLabel id="endedReason-label" shrink={!!endedReason}>
              {t('campaignPage.ongoingCallsTable.filter.endReason')}
            </InputLabel>
            <CustomSelect
              id="endedReason-select"
              labelId="endedReason-label"
              input={
                <OutlinedInput
                  label={t('campaignPage.ongoingCallsTable.filter.endReason')}
                  notched={!!endedReason}
                />
              }
              value={endedReason ?? ''}
              name="endedReason"
              displayEmpty
              IconComponent={IconChevronDown}
              onChange={(event: SelectChangeEvent) =>
                setValue('endedReason', event.target.value as CallEndedReasonsType)
              }
              renderValue={(value: string) =>
                renderSelectValue(value, t('campaignPage.ongoingCallsTable.filter.endReason'))
              }>
              {Object.values(CallEndedReasons).map((filter) => (
                <MenuItem key={filter} value={filter}>
                  {capitalize(filter).replaceAll('-', ' ')}
                </MenuItem>
              ))}
            </CustomSelect>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderDatePicker('start', t('campaignPage.ongoingCallsTable.startDate'), start)}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderDatePicker('end', t('campaignPage.ongoingCallsTable.endDate'), end)}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExtendedSearch}
                  onChange={(event) => onExtendedSearchChange(event.target.checked)}
                />
              }
              label={t('campaignPage.ongoingCallsTable.filter.extendedSearch')}
            />
            {isExtendedSearch && (
              <TextField
                fullWidth
                id="call-id-search-input"
                placeholder={t('campaignPage.ongoingCallsTable.filter.callIdSearchPlaceholder')}
                size="small"
                value={callIdSearchText}
                onChange={(event) => setCallIdSearchText(event.target.value)}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};
